import type { Grade, Mode, Op, TrailSpec, Dir } from "../types";
import { Rng, dailySeedString, utcDateISO } from "../rng";
import {
  artForDay,
  artForLevel,
  gridSize,
  pathLength,
  skillsForCampaign,
  skillsForPractice,
  timeLimitMs,
  dirBetween,
} from "./config";
import { generatePath } from "./path";
import { generateProblemSafe, stonesFor, tokensToPlain } from "../math/engine";

export interface TrailOpts {
  mode: Mode;
  grade: Grade;
  level?: number | null;
  op?: Op | null;
  extraTime?: boolean;
  seed?: string;
}

export function makeTrail(opts: TrailOpts): TrailSpec {
  const grade = opts.grade;
  const mode = opts.mode;
  const level = opts.level ?? (mode === "campaign" ? 1 : null);
  const size = gridSize(grade);
  const len = pathLength(grade);
  let seed = opts.seed;
  if (!seed) {
    if (mode === "daily") seed = dailySeedString(utcDateISO(), grade);
    else seed = `${mode}|${grade}|${level ?? 0}|${opts.op ?? "x"}|${Date.now()}|${Math.random()}`;
  }
  const rng = new Rng(seed);
  const path = generatePath(size, len, rng);

  const skills =
    mode === "practice" && opts.op
      ? skillsForPractice(grade, opts.op)
      : skillsForCampaign(grade, level ?? 10);

  const steps = [];
  let lastDir: Dir | null = null;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    const correctDir = dirBetween(a, b);
    if (!correctDir) throw new Error("Path not adjacent");
    const problem = generateProblemSafe(skills, grade, rng);
    const stones = stonesFor(problem, correctDir, rng, lastDir);
    steps.push({ cell: a, problem, stones, correctDir });
    lastDir = correctDir;
  }

  const dayNum = Number(utcDateISO().replace(/-/g, "")) + grade;
  const art = mode === "campaign" && level ? artForLevel(level) : artForDay(dayNum + len);

  return {
    seed,
    mode,
    grade,
    level: level,
    op: opts.op ?? null,
    size,
    pathLength: len,
    timeLimitMs: timeLimitMs(grade, !!opts.extraTime, mode),
    art: art.file,
    artTitle: art.title,
    path,
    steps,
  };
}

export function exprOf(spec: TrailSpec, step: number): string {
  return tokensToPlain(spec.steps[step].problem.tokens);
}
